import React, { useEffect, useState } from 'react';
import DateRangePicker, { DateRangePreset } from '@components/common/DateRangePicker';
import Table, { TableHeaderContainerV2 } from '@components/common/Table';
import PaginationComponent from '@components/pagination';
import { userLogColumn } from '@data/columns';
import { getUserLogV2 } from '@lib/api/v2/log';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import Format from '@util/formatter';

interface LogTabProps {
  phoneNum: string;
  setIsLoading: (loading: boolean) => void;
}

const LogTab = ({ phoneNum, setIsLoading }: LogTabProps) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();

  const [startDate, setStartDate] = useState(dayjs().startOf('week').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().endOf('week').format('YYYY-MM-DD'));
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [totalLength, setTotalLength] = useState(0);
  const [dataList, setDataList] = useState<any[]>([]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const dateRange = Format.formatDateRange(startDate, endDate);
      const { data } = await createRequest(getUserLogV2, {
        page: currentPage,
        limit: postsPerPage,
        start: dateRange.start,
        end: dateRange.end,
        search: phoneNum,
      });
      setDataList(
        data.result.map((item, index) => ({
          ...item,
          idx: item.id,
          id: item.id * index,
          create: dayjs(item.updatedAt).format('YYYY-MM-DD HH:mm:ss'),
          seatNumber1: `${item.seatNumber ? Format.formatNumber(item.seatNumber) : '-'}번`,
        })),
      );
      if (data.count > 0) setTotalLength(data.count);
    } catch (error) {
      catchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (phoneNum) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNum, currentPage, startDate, endDate]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') setStartDate(value);
    else if (name === 'endDate') setEndDate(value);
    setCurrentPage(1);
  };

  return (
    <>
      <TableHeaderContainerV2>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          defaultPreset={DateRangePreset.THIS_WEEK}
          maxPeriod={DateRangePreset.YEAR}
        />
      </TableHeaderContainerV2>
      <div className="datatable-container">
        <Table tableHeight={500} data={dataList} columns={userLogColumn} striped={true} />
      </div>
      {totalLength > 0 && (
        <PaginationComponent
          setCurrentPage={setCurrentPage}
          totalLength={totalLength}
          postsPerPage={postsPerPage}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default LogTab;
